import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Auth/Login';
import MainLayout from './layouts/MainLayout';

// Lazy load pages for better performance
const Dashboard = React.lazy(() => import('./pages/Dashboard/Dashboard'));
const LeadList = React.lazy(() => import('./pages/Leads/LeadList'));
const LeadForm = React.lazy(() => import('./pages/Leads/LeadForm'));
const LeadDetail = React.lazy(() => import('./pages/Leads/LeadDetail'));
const QuotationList = React.lazy(() => import('./pages/Quotations/QuotationList'));
const QuotationForm = React.lazy(() => import('./pages/Quotations/QuotationForm'));
const QuotationPreview = React.lazy(() => import('./pages/Quotations/QuotationPreview'));
const ProformaList = React.lazy(() => import('./pages/Proforma/ProformaList'));
const ProformaForm = React.lazy(() => import('./pages/Proforma/ProformaForm'));
const ProformaPreview = React.lazy(() => import('./pages/Proforma/ProformaPreview'));
const ProformaPDF = React.lazy(() => import('./pages/Proforma/ProformaPDF'));
const OrderList = React.lazy(() => import('./pages/Orders/OrderList'));
const OrderDetail = React.lazy(() => import('./pages/Orders/OrderDetail'));
const OrderForm = React.lazy(() => import('./pages/Orders/OrderForm'));
const OrderPDF = React.lazy(() => import('./pages/Orders/OrderPDF'));
const CustomerList = React.lazy(() => import('./pages/Customers/CustomerList'));
const CustomerProfile = React.lazy(() => import('./pages/Customers/CustomerProfile'));
const CustomerForm = React.lazy(() => import('./pages/Customers/CustomerForm'));
const VisitList = React.lazy(() => import('./pages/Visits/VisitList'));
const VisitForm = React.lazy(() => import('./pages/Visits/VisitForm'));
const VisitDetail = React.lazy(() => import('./pages/Visits/VisitDetail'));
const FollowUpList = React.lazy(() => import('./pages/FollowUp/FollowUpList'));
const FollowUpForm = React.lazy(() => import('./pages/FollowUp/FollowUpForm'));
const LeadReport = React.lazy(() => import('./pages/Reports/LeadReport'));
const SalesReport = React.lazy(() => import('./pages/Reports/SalesReport'));
const QuotationReports = React.lazy(() => import('./pages/Reports/QuotationReports'));
const OrderReports = React.lazy(() => import('./pages/Reports/OrderReports'));
const CustomerReports = React.lazy(() => import('./pages/Reports/CustomerReports'));
const ProductReports = React.lazy(() => import('./pages/Reports/ProductReports'));
const FinancialReports = React.lazy(() => import('./pages/Reports/FinancialReports'));
const TerritoryReports = React.lazy(() => import('./pages/Reports/TerritoryReports'));
const UserManagement = React.lazy(() => import('./pages/Settings/UserManagement'));
const ProductMaster = React.lazy(() => import('./pages/Settings/ProductMaster'));
const TerritoryManagement = React.lazy(() => import('./pages/Settings/TerritoryManagement'));
const DispatchList = React.lazy(() => import('./pages/Dispatch/DispatchList'));
const DispatchDetail = React.lazy(() => import('./pages/Dispatch/DispatchDetail'));
const DispatchForm = React.lazy(() => import('./pages/Dispatch/DispatchForm'));
const DeliveryTracking = React.lazy(() => import('./pages/Dispatch/DeliveryTracking'));
const Notification = React.lazy(() => import('./pages/NotificationsPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const SecurityPage = React.lazy(() => import('./pages/SecurityPage'));
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="mt-3 text-sm text-slate-500">Loading...</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public Routes - No Layout */}
          <Route path="/login" element={<Login />} />

          {/* Protected Routes - With MainLayout */}
          <Route path="/" element={
            <ProtectedRoute>
              <MainLayout>
                <Navigate to="/dashboard" replace />
              </MainLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/notifications" element={
            <ProtectedRoute>
              <MainLayout>
                <Notification />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/security" element={
            <ProtectedRoute>
              <MainLayout>
                <SecurityPage />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/settings" element={
            <ProtectedRoute>
              <MainLayout>
                <SettingsPage />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/profile" element={
            <ProtectedRoute>
              <MainLayout>
                <ProfilePage />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/dashboard" element={
            <ProtectedRoute>
              <MainLayout>
                <Dashboard />
              </MainLayout>
            </ProtectedRoute>
          } />

          {/* Leads */}
          <Route path="/leads" element={
            <ProtectedRoute>
              <MainLayout>
                <LeadList />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/leads/new" element={
            <ProtectedRoute>
              <MainLayout>
                <LeadForm />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/leads/:id/edit" element={
            <ProtectedRoute>
              <MainLayout>
                <LeadForm />
              </MainLayout>
            </ProtectedRoute>
          } />

          {/* Quotations */}
          <Route path="/quotations" element={
            <ProtectedRoute>
              <MainLayout>
                <QuotationList />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/quotations/new" element={
            <ProtectedRoute>
              <MainLayout>
                <QuotationForm />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/quotations/:id" element={
            <ProtectedRoute>
              <MainLayout>
                <QuotationPreview />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/quotations/:id/edit" element={
            <ProtectedRoute>
              <MainLayout>
                <QuotationForm />
              </MainLayout>
            </ProtectedRoute>
          } />

          {/* Proforma Invoices */}
          <Route path="/proforma" element={
            <ProtectedRoute>
              <MainLayout>
                <ProformaList />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/proforma/new" element={
            <ProtectedRoute>
              <MainLayout>
                <ProformaForm />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/proforma/:id" element={
            <ProtectedRoute>
              <MainLayout>
                <ProformaForm />
              </MainLayout>
            </ProtectedRoute>
          } />

          {/* Orders */}
          <Route path="/orders" element={
            <ProtectedRoute>
              <MainLayout>
                <OrderList />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/orders/:id" element={
            <ProtectedRoute>
              <MainLayout>
                <OrderDetail />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/orders/:id/edit" element={
            <ProtectedRoute>
              <MainLayout>
                <OrderForm />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/orders/:id/pdf" element={
            <ProtectedRoute>
              <MainLayout>
                <OrderPDF />
              </MainLayout>
            </ProtectedRoute>
          } />

          {/* Customers */}
          <Route path="/customers" element={
            <ProtectedRoute>
              <MainLayout>
                <CustomerList />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/customers/new" element={
            <ProtectedRoute>
              <MainLayout>
                <CustomerForm />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/customers/:id" element={
            <ProtectedRoute>
              <MainLayout>
                <CustomerProfile />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/customers/:id/edit" element={
            <ProtectedRoute>
              <MainLayout>
                <CustomerForm />
              </MainLayout>
            </ProtectedRoute>
          } />

          {/* Visits */}
          <Route path="/visits" element={
            <ProtectedRoute>
              <MainLayout>
                <VisitList />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/visits/new" element={
            <ProtectedRoute>
              <MainLayout>
                <VisitForm />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/visits/:id" element={
            <ProtectedRoute>
              <MainLayout>
                <VisitDetail />
              </MainLayout>
            </ProtectedRoute>
          } />

          {/* Follow-ups */}
          <Route path="/followups" element={
            <ProtectedRoute>
              <MainLayout>
                <FollowUpList />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/followups/new" element={
            <ProtectedRoute>
              <MainLayout>
                <FollowUpForm />
              </MainLayout>
            </ProtectedRoute>
          } />

          {/* Reports */}
          <Route path="/reports/leads" element={
            <ProtectedRoute>
              <MainLayout>
                <LeadReport />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/reports/sales" element={
            <ProtectedRoute>
              <MainLayout>
                <SalesReport />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/reports/territory" element={
            <ProtectedRoute>
              <MainLayout>
                <TerritoryReports />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/reports/quotations" element={
            <ProtectedRoute>
              <MainLayout>
                <QuotationReports />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/reports/orders" element={
            <ProtectedRoute>
              <MainLayout>
                <OrderReports />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/reports/customers" element={
            <ProtectedRoute>
              <MainLayout>
                <CustomerReports />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/reports/products" element={
            <ProtectedRoute>
              <MainLayout>
                <ProductReports />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/reports/financial" element={
            <ProtectedRoute>
              <MainLayout>
                <FinancialReports />
              </MainLayout>
            </ProtectedRoute>
          } />

          {/* Settings */}
          <Route path="/settings/users" element={
            <ProtectedRoute>
              <MainLayout>
                <UserManagement />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/settings/products" element={
            <ProtectedRoute>
              <MainLayout>
                <ProductMaster />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/settings/territory" element={
            <ProtectedRoute>
              <MainLayout>
                <TerritoryManagement />
              </MainLayout>
            </ProtectedRoute>
          } />

          {/* Dispatch */}
          <Route path="/dispatch" element={
            <ProtectedRoute>
              <MainLayout>
                <DispatchList />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/dispatch/:id" element={
            <ProtectedRoute>
              <MainLayout>
                <DispatchDetail />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/dispatch/:id/edit" element={
            <ProtectedRoute>
              <MainLayout>
                <DispatchForm />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/dispatch/tracking" element={
            <ProtectedRoute>
              <MainLayout>
                <DeliveryTracking />
              </MainLayout>
            </ProtectedRoute>
          } />

          {/* 404 - Not Found */}
          <Route path="*" element={
            <MainLayout>
              <div className="flex flex-col items-center justify-center h-full">
                <h1 className="text-6xl font-bold text-slate-200">404</h1>
                <h2 className="text-2xl font-semibold text-slate-700 mt-2">Page Not Found</h2>
                <p className="text-slate-500 mt-1">The page you're looking for doesn't exist.</p>
                <a href="/dashboard" className="inline-block mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                  Go to Dashboard
                </a>
              </div>
            </MainLayout>
          } />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}

export default App;